import React from 'react';
import { Box, Typography } from '@mui/material';
import { formatMoney } from '../utils/money';

/**
 * Choosing a credit pack.
 *
 * ── WHAT THIS REPLACES, TWICE ───────────────────────────────────────────────
 * First it replaced three buttons — Buy Content Credits / Buy Image Credits /
 * Buy List Credits — that looked like three products but were one: each sent
 * `quantity: 10` with a different `credit_type`, and since the pools merged the
 * server maps every credit_type onto the same weighted balance. They also could
 * not state a price, because the old call asked for a raw quantity that the
 * server rounded to the nearest package after the click.
 *
 * Now the tiles themselves are gone. They BOUGHT on click, which put an
 * irreversible payment one stray tap away and left no room for the things that
 * belong beside the decision — which card is being charged, and what the total
 * is. Selection and payment are separate steps: this component only chooses.
 *
 * ── PRICES COME FROM THE SERVER, AT THIS ACCOUNT'S TIER ─────────────────────
 * `price_cents` is already resolved for the viewer — subscribers see the lower
 * rate — so nothing here decides what anything costs. The server charges from
 * the same resolution, so the figure shown and the figure taken cannot differ.
 */

const TEXT = '#1A1A1A';
const TEXT_MUTED = '#8A8F85';
const TEAL = '#1C7C73';
const CARD_BORDER = '#E5E7E0';

export default function CreditPackagePicker({
  packages = [],
  selectedId = null,
  onSelect,
  disabled = false,
}) {
  // Nothing to sell until the rate table resolves. An empty row of outlines
  // would suggest the feature is broken rather than still loading.
  if (!Array.isArray(packages) || packages.length === 0) return null;

  return (
    <Box
      role="radiogroup"
      aria-label="Credit packs"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}
    >
      {packages.map((pkg) => {
        const selected = selectedId === pkg.id;
        const pieces = Number(pkg.approx_pieces) || 0;
        return (
          <Box
            key={pkg.id}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onSelect?.(pkg.id)}
            disabled={disabled}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              textAlign: 'left',
              width: '100%',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.55 : 1,
              bgcolor: '#FFFFFF',
              border: `1px solid ${selected ? TEAL : CARD_BORDER}`,
              boxShadow: selected ? `0 0 0 1px ${TEAL}` : 'none',
              borderRadius: '12px',
              p: '14px 16px',
              font: 'inherit',
              transition: 'border-color .15s, box-shadow .15s',
              '&:hover': disabled ? {} : { borderColor: TEAL },
            }}
          >
            {/* A drawn radio rather than an <input>: the whole row is the
                control, and a real input inside a button is invalid markup.
                role/aria-checked above carry the semantics instead. */}
            <Box
              aria-hidden
              sx={{
                mt: '2px',
                flex: '0 0 auto',
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: `2px solid ${selected ? TEAL : '#C4C8C0'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {selected && (
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: TEAL }} />
              )}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 15, color: TEXT,
                }}
              >
                {/* Singular for a one-credit pack: "1 credits — $0.50" is the
                    kind of detail that reads as carelessness on the screen
                    where someone is deciding whether to hand over money. Same
                    idiom as the piece count below it, and as the per-action
                    costs on the Credits page. */}
                {Number(pkg.credits).toLocaleString()} credit{Number(pkg.credits) === 1 ? '' : 's'} — {formatMoney(pkg.price_cents, 'usd')}
              </Typography>
              {/* The server's own estimate, derived from the live cost table, so
                  this count follows a pricing change instead of going stale. */}
              {pieces > 0 && (
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_MUTED, mt: 0.25,
                  }}
                >
                  About {pieces} piece{pieces === 1 ? '' : 's'} of content with an AI image
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
